/**
 * CreateIdeaModal Component
 *
 * Modal for creating or editing an idea.
 */

import React, { useState, useEffect } from "react";
import { Modal } from "@/components/ui/Modal";
import {
  Idea,
  IdeaContentType,
  IdeaResource,
  CreateIdeaRequest,
} from "../types";
import { ENTITY_CONFIG } from "@/core/entityConfig";
import { getIdeaConcept } from "../ideaHelpers";
import { useObjectiveTypes } from "../services/objectiveTypes";
import { OptionDropdown } from "@/components/ui/OptionDropdown";
import type { DropdownOption } from "@/components/ui/OptionDropdown";
import { IDEA_CONTENT_TYPE_OPTIONS } from "../ideaContentTypeConfig";

interface CreateIdeaModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateIdeaRequest) => void;
  idea?: Idea | null;
  isLoading?: boolean;
}

// "" clears the selection, mirroring the old select's empty option.
const CONTENT_TYPE_PICKER_OPTIONS: DropdownOption<IdeaContentType | "">[] = [
  { value: "", label: "Select type..." },
  ...IDEA_CONTENT_TYPE_OPTIONS,
];

export const CreateIdeaModal: React.FC<CreateIdeaModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  idea,
  isLoading = false,
}) => {
  const [title, setTitle] = useState("");
  const [concept, setConcept] = useState("");
  const [contentType, setContentType] = useState<IdeaContentType | "">("");
  const [objectiveType, setObjectiveType] = useState<string>("");
  const [resources, setResources] = useState<IdeaResource[]>([]);
  const [newResourceUrl, setNewResourceUrl] = useState("");
  const [newResourceNote, setNewResourceNote] = useState("");

  const { data: objectiveTypes = [] } = useObjectiveTypes();

  const isEditing = !!idea;

  useEffect(() => {
    if (idea) {
      setTitle(idea.title);
      setConcept(getIdeaConcept(idea) || "");
      setContentType(idea.contentType || "");
      setObjectiveType(idea.objectiveType || "");
      setResources(idea.resources || []);
    } else {
      setTitle("");
      setConcept("");
      setContentType("");
      setObjectiveType("");
      setResources([]);
    }
  }, [idea, isOpen]);

  const handleAddResource = () => {
    if (!newResourceUrl.trim()) return;
    setResources([
      ...resources,
      { url: newResourceUrl.trim(), note: newResourceNote.trim() || undefined },
    ]);
    setNewResourceUrl("");
    setNewResourceNote("");
  };

  const handleRemoveResource = (index: number) => {
    setResources(resources.filter((_, i) => i !== index));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;

    // Include any pending resource that hasn't been explicitly added
    const finalResources = [...resources];
    if (newResourceUrl.trim()) {
      finalResources.push({
        url: newResourceUrl.trim(),
        note: newResourceNote.trim() || undefined,
      });
    }

    const data: CreateIdeaRequest = {
      title: title.trim(),
      concept: concept.trim() || undefined,
      contentType: contentType || undefined,
      objectiveType: objectiveType || undefined,
      resources: finalResources.length > 0 ? finalResources : undefined,
    };

    onSubmit(data);
  };

  const footer = (
    <>
      <button
        type="button"
        onClick={onClose}
        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        disabled={isLoading}
      >
        Cancel
      </button>
      <button
        type="submit"
        form="create-idea-form"
        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
        disabled={isLoading || !title.trim()}
      >
        {isLoading
          ? "Saving..."
          : isEditing
            ? "Save Changes"
            : ENTITY_CONFIG.idea.createLabel}
      </button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? "Edit Idea" : ENTITY_CONFIG.idea.createLabel}
      size="lg"
      footer={footer}
    >
      <form id="create-idea-form" onSubmit={handleSubmit} className="p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Title *
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Enter idea title"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Content Type
          </label>
          <OptionDropdown
            value={contentType}
            options={CONTENT_TYPE_PICKER_OPTIONS}
            onChange={setContentType}
            variant="input"
          />
        </div>

        {objectiveTypes.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Objective
            </label>
            <OptionDropdown
              value={objectiveType}
              options={[
                { value: "", label: "Select objective..." },
                ...objectiveTypes.map((opt) => ({
                  value: opt.key,
                  label: opt.label,
                })),
              ]}
              onChange={setObjectiveType}
              variant="input"
            />
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Concept
          </label>
          <textarea
            value={concept}
            onChange={(e) => setConcept(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Describe the idea concept..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Resources
          </label>
          <div className="space-y-2">
            {resources.map((resource, index) => (
              <div
                key={index}
                className="flex items-center gap-2 p-2 bg-gray-50 rounded-sm"
              >
                <div className="flex-1 min-w-0">
                  <a
                    href={resource.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:underline truncate block"
                  >
                    {resource.url}
                  </a>
                  {resource.note && (
                    <span className="text-xs text-gray-500">
                      {resource.note}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveResource(index)}
                  className="text-red-500 hover:text-red-700"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            ))}

            <div className="flex gap-2">
              <input
                type="url"
                value={newResourceUrl}
                onChange={(e) => setNewResourceUrl(e.target.value)}
                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Resource URL"
              />
              <input
                type="text"
                value={newResourceNote}
                onChange={(e) => setNewResourceNote(e.target.value)}
                className="w-32 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Note"
              />
              <button
                type="button"
                onClick={handleAddResource}
                className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
              >
                Add
              </button>
            </div>
          </div>
        </div>
      </form>
    </Modal>
  );
};
